/**
 * Journey Navigator - Main Journey Detail Page
 *
 * Interactive journey detail view featuring:
 * - Journey header with metadata and status
 * - Asset manifest
 * - Station track with progress indicators
 * - Current station panel (Inspector pane) with action buttons
 * - Recommendations sidebar
 * - Journey map export
 *
 * DC-1587 (Epic F): retired the "Captain" / "train" metaphor from
 * user-visible copy. The Journey type's `captain: {...}` field stays as
 * the API contract until v1.3 to avoid a server-side rename.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ListChecks,
  ClipboardList,
  Compass,
  AlertCircle,
  Sparkle,
  Lightbulb,
  MousePointerClick,
  Boxes,
  FileText,
  ArrowLeft,
  Target,
} from 'lucide-react';
import { TrainVisualization } from '../../components/TrainVisualization';
import { DataQualityScorecard } from '../../components/station-results/DataQualityScorecard';
import { MLReadinessLegacyWrapper } from '../../components/station-results/MLReadinessLegacyWrapper';
import type { Station } from '../../components/StationCard';
import { StrategicIntelligenceDashboard } from '../../components/StrategicIntelligenceDashboard';
import { ActionPlanPanel } from '../../components/ActionPlanPanel';
import { JourneyMaturityPanel } from '../../components/JourneyMaturityPanel';
import { JourneyGovernancePanel } from '../../components/JourneyGovernancePanel';
import { AssetDetailPanel } from '../../components/AssetDetailPanel';
import { OrgContextBar } from '../../components/OrgContextBar';
import { ContextualFitPanel } from '../../components/contextual-validity';
import { StationRunList } from './StationRunList';
import { JourneySettingsPanel } from './JourneySettingsPanel';
import { SemanticClarityPanel } from '../SemanticClarityView';
import { StationActionPanel } from './StationActionPanel';
import { useApiClient } from '../../hooks/useApiClient';
import { JourneyReportButton } from '../../components/reports/JourneyReportButton';
import { JourneyProvider } from '../../contexts/JourneyContext';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { RecommendationList } from '../../components/RecommendationList';
import { EvaluateContent } from '../EvaluatePage';
import type { DomainSummary } from '../../types/triangulation';
import '../../styles/journey.css';
import { JourneyStatusPanel } from '../../components/journey/JourneyStatusPanel';
import { JourneyLifecycleActions } from '../../components/journey/JourneyLifecycleActions';
import {
  WorkflowDeclarationDialog,
  type WorkflowComponentPreview,
} from '../../components/journey/WorkflowDeclarationDialog';
import { useAuth } from '../../contexts/AuthContext';
import type { JourneyStatusSummary } from '../../types/journeyStatus';

type JourneyTab =
  | 'bridge'
  | 'stations'
  | 'fair'
  | 'data-quality'
  | 'ml-readiness'
  | 'ontology-inference'
  | 'governance'
  | 'maturity'
  | 'contextual-validity'
  | 'recommendations'
  | 'action-plan'
  | 'settings';

const JOURNEY_TABS: JourneyTab[] = [
  'bridge',
  'stations',
  'fair',
  'data-quality',
  'ml-readiness',
  'ontology-inference',
  'governance',
  'maturity',
  'contextual-validity',
  'recommendations',
  'action-plan',
  'settings',
];

// v2.0: the 12-tab bar is gone (left rail is the menu). Surfaces are reached
// from Home's detail chips, the station run list, and Bridge AI; this map only
// labels the slim back-link header shown on a non-Navigator surface.
const SURFACE_LABELS: Record<JourneyTab, string> = {
  bridge: 'Strategic Intelligence',
  stations: 'Navigator',
  fair: 'FAIR',
  'data-quality': 'Data Quality',
  'ml-readiness': 'ML Readiness',
  'ontology-inference': 'Ontology Inference',
  governance: 'Governance',
  maturity: 'Maturity',
  'contextual-validity': 'Contextual Validity',
  recommendations: 'Recommendations',
  'action-plan': 'Action Plan',
  settings: 'Settings',
};

// ============================================================================
// TYPES
// ============================================================================

type JourneyStatus = 'draft' | 'in_progress' | 'completed' | 'abandoned';

interface Asset {
  asset_id: string;  // Server uses asset_id, not id
  type: 'dataset' | 'interview' | 'document';
  name: string;
  location: string;  // Server uses location, not file_path
  created_at: string;  // Server uses created_at, not uploaded_at
  health_score?: number;
  status: 'pending' | 'processing' | 'ready' | 'error';
}

interface JourneyDetails {
  group_id: string;
  name: string;
  description?: string;
  captain: {
    user_id: string;
    name: string;
    email: string;
  };
  status: JourneyStatus;
  created_at: string;
  updated_at: string;
  assets: Asset[];
  stations: Station[];
  // DC-1617: the triangulation-domain assignment is surfaced on read via
  // metadata.domain_id (the store mapper sources it from the authoritative
  // domain_id column). Used to show the Domain tile in the header.
  metadata?: {
    domain_id?: string | null;
  };
}

/**
 * Collapse duplicate station rows to one per station_id. Legacy journeys carry
 * BOTH a real (analyzed, job-bearing) station row and an empty duplicate — which
 * double-rendered the run list (React duplicate-key warnings) and double-counted
 * "N of N stations". Keep the richest row per station (one with a job/results),
 * falling back to completed status; original order is preserved.
 */
function stationRichness(s: Station): number {
  return (
    (s.job_id ? 4 : 0) +
    (s.results && Object.keys(s.results).length > 0 ? 2 : 0) +
    ((s.assets_processed?.length ?? 0) > 0 ? 1 : 0) +
    (s.status === 'completed' ? 1 : 0)
  );
}

function dedupeStations(stations: Station[]): Station[] {
  const byId = new Map<string, Station>();
  for (const s of stations) {
    const existing = byId.get(s.station_id);
    if (!existing || stationRichness(s) > stationRichness(existing)) {
      byId.set(s.station_id, s);
    }
  }
  return Array.from(byId.values());
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const JourneyNavigator: React.FC = () => {
  const { group_id } = useParams<{ group_id: string }>();
  const navigate = useNavigate();
  const { authFetch, get } = useApiClient();
  const { user } = useAuth();

  const [journey, setJourney] = useState<JourneyDetails | null>(null);
  // v2.5 SS-02/B2: the canonical status contract (ONE derivation, shared with
  // the portfolio list). Null = server could not derive it — rendered as
  // "unavailable", never fabricated.
  const [statusSummary, setStatusSummary] = useState<JourneyStatusSummary | null>(null);
  const [declaringWorkflow, setDeclaringWorkflow] = useState(false);
  const [domainName, setDomainName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // Initial tab can be overridden via ?tab=fair on the URL — used when the
  // FAIR station card on the Stations tab redirects here in-window.
  const initialTab = (searchParams.get('tab') as JourneyTab | null);
  const [currentStationId, setCurrentStationId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<JourneyTab>(
    initialTab && JOURNEY_TABS.includes(initialTab) ? initialTab : 'stations',
  );
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [selectedFairAssetId, setSelectedFairAssetId] = useState<string | null>(null);
  const [governanceRefreshTrigger, setGovernanceRefreshTrigger] = useState(0); // Increment to refresh governance indicators
  const [exportError, setExportError] = useState<string | null>(null);
  /**
   * Whether an Action Plan EXISTS for this journey (S4e) — four states, because
   * "could not check" is not "you have none". Mirrors the Bridge-prerequisite
   * pattern (e054f73): `checking` while the probe is in flight, `exists` on 200,
   * `absent` on 404, `unavailable` on anything else. The probe is the lightweight
   * `/action-plan/summary` metadata read, not the full plan document.
   */
  const [actionPlanEntry, setActionPlanEntry] = useState<
    'checking' | 'exists' | 'absent' | 'unavailable'
  >('checking');

  // DC-1592 — two-way ?tab= sync so deep-linking, URL sharing, refresh, and
  // browser Back/Forward all behave correctly.
  //
  // The previous implementation DELETED ?tab= from the URL after consuming
  // it on mount, which killed three things:
  //   - Refresh resets to default 'stations' tab (URL had no ?tab= anymore)
  //   - URL sharing was impossible
  //   - Back/Forward across tabs did not work — clicking a tab updated
  //     internal state but never the URL, so popstate had nothing to restore
  //
  // New shape: one effect for URL → state (Back/Forward + deep-link), one
  // effect for state → URL (tab-click sync). First-mount alignment uses
  // replace to avoid polluting history with the default; subsequent tab
  // clicks use push so Back/Forward navigates between tabs.
  const tabSyncMounted = useRef(false);

  useEffect(() => {
    const tab = searchParams.get('tab') as JourneyTab | null;
    if (tab && JOURNEY_TABS.includes(tab) && tab !== activeTab) {
      setActiveTab(tab);
    }
    // No `else` to default — if URL has no/invalid ?tab=, leave activeTab
    // wherever the user (or the state→URL effect on next tick) puts it.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- activeTab handled by sibling effect
  }, [searchParams]);

  useEffect(() => {
    const currentInUrl = searchParams.get('tab');
    if (currentInUrl === activeTab) return;
    const next = new URLSearchParams(searchParams);
    next.set('tab', activeTab);
    // First mount: align the URL with the initial state without creating a
    // history entry the user can Back out of. Subsequent clicks push so
    // Back/Forward navigates between tabs.
    setSearchParams(next, { replace: !tabSyncMounted.current });
    tabSyncMounted.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- searchParams handled by sibling effect
  }, [activeTab]);

  useEffect(() => {
    if (group_id) {
      fetchJourneyDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only fetch on group_id change
  }, [group_id]);

  // Auto-refresh journey data every 2 seconds if any station is in_progress
  useEffect(() => {
    if (!journey) return;

    const hasRunningStations = journey.stations?.some(s => s.status === 'in_progress');

    if (hasRunningStations) {
      console.log('[JourneyNavigator] Station running, starting auto-refresh polling (2s interval)');
      const intervalId = setInterval(() => {
        console.log('[JourneyNavigator] Polling for station updates...');
        fetchJourneyDetails(true); // Pass true to indicate background refresh
      }, 2000);

      return () => {
        console.log('[JourneyNavigator] Stopping auto-refresh polling');
        clearInterval(intervalId);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Polling depends on journey state
  }, [journey, group_id]);

  // Resolve the triangulation domain name for the header tile. The journey API
  // returns only metadata.domain_id; map it to a name via the cached domains
  // list (same endpoint DomainSelect uses, so this is usually a cache hit).
  useEffect(() => {
    const domainId = journey?.metadata?.domain_id;
    if (!domainId) {
      setDomainName(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const domains = await get<DomainSummary[]>('/api/triangulation/domains');
        if (cancelled) return;
        const match = Array.isArray(domains)
          ? domains.find((d) => d.id === domainId)
          : undefined;
        setDomainName(match?.name ?? null);
      } catch {
        if (!cancelled) setDomainName(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [journey?.metadata?.domain_id, get]);

  const fetchJourneyDetails = async (isBackgroundRefresh = false) => {
    try {
      // Only show loading spinner on initial load, not during background polling
      if (!isBackgroundRefresh) {
        setLoading(true);
      }
      setError(null);

      const response = await authFetch(`/api/journey/${group_id}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch journey: ${response.statusText}`);
      }

      const responseData = await response.json();
      console.log('[JourneyNavigator] Raw response:', JSON.stringify(responseData).slice(0, 500));
      console.log('[JourneyNavigator] Has journey wrapper:', !!responseData.journey);
      console.log('[JourneyNavigator] Journey stations in response:', responseData.journey?.stations?.length || 0);

      // Backend returns { journey: Journey, status_summary } wrapper
      const data: JourneyDetails = responseData.journey || responseData;
      setStatusSummary((responseData.status_summary as JourneyStatusSummary | null) ?? null);
      console.log('[JourneyNavigator] Stations:', data.stations);
      console.log('[JourneyNavigator] Stations count:', data.stations?.length);
      // Collapse the legacy asset-less duplicate station rows before anything
      // counts or renders them (fixes double-render + the "8/8 vs 4/4" count).
      if (data.stations) {
        data.stations = dedupeStations(data.stations);
      }
      setJourney(data);

      // Auto-select first incomplete station (only on initial load)
      if (!isBackgroundRefresh && data.stations && data.stations.length > 0) {
        const firstIncomplete = data.stations.find(s => s.status !== 'completed');
        if (firstIncomplete) {
          setCurrentStationId(firstIncomplete.station_id);
        }
      }
    } catch (err: unknown) {
      console.error('[JourneyNavigator] Fetch error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      if (!isBackgroundRefresh) {
        setLoading(false);
      }
    }
  };

  const handleStationClick = (stationId: string) => {
    setCurrentStationId(stationId);
  };

  /*
   * S4e: probe whether an Action Plan exists so the journey entry can be
   * STATE-AWARE — "Generate Action Plan" when absent, "Open Action Plan" when
   * present. A 404 is the only evidence of absence; a failed probe is
   * `unavailable` and the entry says it could not check rather than claiming
   * there is no plan. Re-probed when the reader returns from the Action Plan
   * tab, since they may have just generated one.
   */
  useEffect(() => {
    if (!group_id) return;
    let cancelled = false;
    setActionPlanEntry('checking');
    (async () => {
      try {
        const response = await authFetch(`/api/journeys/${group_id}/action-plan/summary`);
        if (cancelled) return;
        if (response.ok) setActionPlanEntry('exists');
        else if (response.status === 404) setActionPlanEntry('absent');
        else setActionPlanEntry('unavailable');
      } catch {
        if (!cancelled) setActionPlanEntry('unavailable');
      }
    })();
    return () => {
      cancelled = true;
    };
    // activeTab dependency is deliberate: leaving the action-plan tab re-probes.
  }, [group_id, authFetch, activeTab]);

  // v2.5 SS-02/B2 (round 1): declaring a workflow is an ADMIN decision with
  // a review dialog — the exact component set is shown BEFORE anything is
  // written, and a declaration has a persistent AUDITED undo (Withdraw).
  // Role-gated client-side for UX; the server remains the security control.
  const [declareError, setDeclareError] = useState<string | null>(null);
  const [workflowDialog, setWorkflowDialog] = useState<'declare' | 'withdraw' | null>(null);
  const [workflowPreview, setWorkflowPreview] = useState<WorkflowComponentPreview[]>([]);
  const workflowTriggerRef = useRef<HTMLElement | null>(null);
  const statusPanelRef = useRef<HTMLDivElement | null>(null);
  const isAdmin = user?.role === 'admin';

  const restoreWorkflowFocus = (transitioned: boolean) => {
    // isConnected guard: after a successful declare/withdraw the invoker may
    // no longer exist (the panel re-renders) — focus the stable status
    // region instead of losing focus to <body>.
    const trigger = workflowTriggerRef.current;
    if (!transitioned && trigger && trigger.isConnected) {
      trigger.focus();
    } else {
      statusPanelRef.current?.focus();
    }
  };

  const handleOpenDeclareDialog = async () => {
    workflowTriggerRef.current = document.activeElement as HTMLElement | null;
    setDeclareError(null);
    try {
      const response = await authFetch(`/api/journey/${group_id}/workflow/default`);
      if (!response.ok) throw new Error(`Could not load the default workflow (${response.status})`);
      const body = await response.json();
      setWorkflowPreview((body.components as WorkflowComponentPreview[]) ?? []);
      setWorkflowDialog('declare');
    } catch (err: unknown) {
      setDeclareError(err instanceof Error ? err.message : 'Could not load the default workflow');
    }
  };

  const handleConfirmWorkflowDialog = async () => {
    setDeclaringWorkflow(true);
    setDeclareError(null);
    try {
      // Round 2: submit EXACTLY the reviewed set — never ask the server to
      // recompute its default after the admin reviewed a specific list.
      const response =
        workflowDialog === 'withdraw'
          ? await authFetch(`/api/journey/${group_id}/workflow`, { method: 'DELETE' })
          : await authFetch(`/api/journey/${group_id}/workflow`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                components: workflowPreview.map((c) => ({
                  component_id: c.component_id,
                  required: c.required,
                })),
              }),
            });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error ?? `Workflow change failed (${response.status})`);
      }
      setWorkflowDialog(null);
      await fetchJourneyDetails(true);
      restoreWorkflowFocus(true);
    } catch (err: unknown) {
      setDeclareError(err instanceof Error ? err.message : 'Workflow change failed');
    } finally {
      setDeclaringWorkflow(false);
    }
  };

  const handleExportMap = async () => {
    try {
      const response = await authFetch(`/api/journey/${group_id}/export-map`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ format: 'markdown' }),
      });

      if (!response.ok) {
        throw new Error('Failed to export journey map');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${group_id}_journey_map.md`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setExportError(null);
    } catch (err: unknown) {
      console.error('[JourneyNavigator] Export error:', err);
      setExportError(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  // ============================================================================
  // RENDER STATES
  // ============================================================================

  if (loading) {
    return (
      <div className="journey-navigator">
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading journey details...</p>
        </div>
      </div>
    );
  }

  if (error || !journey) {
    return (
      <div className="journey-navigator">
        <div className="error-state">
          <AlertCircle size={32} aria-hidden="true" className="error-icon-svg" />
          <h3>Failed to Load Journey</h3>
          <p>{error || 'Journey not found'}</p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button className="btn btn-secondary" onClick={() => navigate('/journey')}>
              ← Back to Dashboard
            </button>
            <button className="btn btn-primary" onClick={() => fetchJourneyDetails()}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentStation = journey.stations.find(s => s.station_id === currentStationId);
  // Operational only (getting-started guide gating) — NEVER a completeness
  // denominator; the status panel is the sole completeness surface.
  const completedStations = journey.stations.filter(s => s.status === 'completed').length;

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <JourneyProvider journeyId={group_id || ''} journeyName={journey.name}>
    <div className="journey-navigator">
      {/* Sprint 9: Organization Context Bar (DC-447) */}
      <OrgContextBar />

      {/* v2.0: the 12-tab bar is retired — the global left rail is the menu.
          On non-Navigator surfaces, a slim back link returns to the journey
          Navigator (stations run list). */}
      {activeTab !== 'stations' && (
        <div className="surface-subnav">
          <button
            type="button"
            className="surface-back"
            onClick={() => setActiveTab('stations')}
          >
            <ArrowLeft size={15} aria-hidden="true" />
            <span>{journey.name}</span>
          </button>
          <span className="surface-sep">/</span>
          <span className="surface-here">{SURFACE_LABELS[activeTab] ?? activeTab}</span>
        </div>
      )}

      {/* Tab Content */}
      <div className="journey-tab-content">
        {/* Bridge Tab - Strategic Intelligence Dashboard (Sprint 9) */}
        {activeTab === 'bridge' && (
          <div id="bridge-panel" role="tabpanel" aria-labelledby="bridge-tab">
            <ErrorBoundary level="section" label="Strategic Intelligence">
            <StrategicIntelligenceDashboard
              onStationClick={(stationId: string) => handleStationClick(stationId)}
              onNavigateToCV={() => setActiveTab('contextual-validity')}
            />
            </ErrorBoundary>
          </div>
        )}

        {/* Navigator (default) — v2.0: not a tabpanel; it's the journey's home
            surface, reached from the left rail / Home, not a tab. */}
        {activeTab === 'stations' && (
          <div id="stations-panel">
            <div className="stations-tab-content">
        <div className="header-breadcrumb">
          <button className="breadcrumb-link" onClick={() => navigate('/journey')}>
            Journeys
          </button>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">{journey.name}</span>
        </div>

        <div className="header-main">
          <div className="header-left">
            <h1>{journey.name}</h1>
            {journey.description && <p className="journey-subtitle">{journey.description}</p>}
            <div className="journey-meta">
              <div className="meta-item">
                <span className="meta-label">Owner</span>
                <span className="meta-value">{journey.captain.name}</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Assets</span>
                <span className="meta-value">{journey.assets.length}</span>
              </div>
              {domainName && (
                <div className="meta-item">
                  <span className="meta-label">Domain</span>
                  <span className="meta-value">{domainName}</span>
                </div>
              )}
            </div>
          </div>

          <div className="header-actions">
            <button
              className="btn btn-secondary header-action"
              onClick={() => setActiveTab('bridge')}
              title="Open the Strategic Intelligence (Bridge AI) summary"
            >
              <Sparkle size={16} aria-hidden="true" />
              <span>Summary</span>
            </button>
            <button
              className="btn btn-secondary header-action"
              onClick={() => navigate(`/journey/${group_id}/recommendations`)}
            >
              <ListChecks size={16} aria-hidden="true" />
              <span>Recommendations</span>
            </button>
            {/*
              * THE ACTION PLAN'S ONLY ENTRY POINT ON THE DEFAULT SURFACE (S5 production).
              *
              * `?tab=action-plan` was routable and the panel rendered, but nothing on this
              * surface linked to it. The two live references were
              * `StrategicIntelligenceDashboard`, which renders inside the Bridge tab, and
              * `JourneySummary`, which is deprecated and not routed at all (see main.tsx).
              * So the Action Plan could only be reached by first entering Bridge AI —
              * including for a reader returning to a plan that already exists.
              *
              * `setActiveTab` rather than a hand-built URL: the state→URL effect above
              * COPIES the existing query string before setting `tab`, so unrelated
              * parameters survive, and it pushes so Back returns here.
              *
              * WORDING: "Action Plan", not "Generate" or "Open". This surface does not
              * fetch the plan, so it does not know whether one exists — and a button that
              * says Generate when a plan is already there, or Open when there is nothing
              * to open, is a small fabricated claim about stored state. The panel itself
              * knows, and says so once it has asked.
              */}
            <button
              className="btn btn-secondary header-action"
              onClick={() => setActiveTab('action-plan')}
              // "Go to", not "Open": the tooltip must not imply a plan exists either.
              title="Go to the Action Plan for this journey"
            >
              <ClipboardList size={16} aria-hidden="true" />
              <span>Action Plan</span>
            </button>
            <button
              className="btn btn-secondary header-action"
              onClick={() => navigate('/scope/global/all/evaluate')}
              title="Review FAIR decisions across all journeys (Bulk Decision matrix)"
            >
              <Compass size={16} aria-hidden="true" />
              <span>Decisions</span>
            </button>
            <button className="btn btn-primary header-action" onClick={handleExportMap}>
              <FileText size={16} aria-hidden="true" />
              <span>Export Journey Map</span>
            </button>
            <JourneyReportButton />
          </div>
        </div>

        {exportError && (
          <div className="error-banner" role="alert">
            <strong>Journey map export failed.</strong>
            <p>{exportError} — try again, or check that the journey has completed stations to export.</p>
            <button className="btn btn-secondary btn-sm" onClick={handleExportMap}>Retry export</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setExportError(null)}>Dismiss</button>
          </div>
        )}

        {/* v2.5 SS-02/B2: the canonical status contract replaces the old
            percentage bar (overall_progress) and hardcoded station counts —
            the SAME derivation the portfolio list renders, so the two
            surfaces cannot disagree. Workflow-unknown shows NO numerics. */}
        <div ref={statusPanelRef} tabIndex={-1}>
          <JourneyStatusPanel
            summary={statusSummary}
            onDeclareWorkflow={isAdmin ? handleOpenDeclareDialog : undefined}
            declareWorkflowBusy={declaringWorkflow}
          />
        </div>
        {isAdmin && statusSummary?.workflowKnown && (
          <button
            type="button"
            className="btn btn-link btn-sm jsp-withdraw-link"
            onClick={(e) => {
              workflowTriggerRef.current = e.currentTarget;
              setDeclareError(null);
              setWorkflowDialog('withdraw');
            }}
          >
            Withdraw workflow declaration…
          </button>
        )}
        {declareError && !workflowDialog && (
          <div className="jsp-action-error" role="alert">
            {declareError}
          </div>
        )}
        {statusSummary && (
          <JourneyLifecycleActions
            groupId={group_id || ''}
            journeyName={journey.name}
            summary={statusSummary}
            onTransitioned={() => fetchJourneyDetails(true)}
          />
        )}
        {workflowDialog && (
          <WorkflowDeclarationDialog
            mode={workflowDialog}
            journeyName={journey.name}
            components={workflowPreview}
            busy={declaringWorkflow}
            error={declareError}
            onConfirm={handleConfirmWorkflowDialog}
            onCancel={() => {
              setWorkflowDialog(null);
              setDeclareError(null);
              restoreWorkflowFocus(false);
            }}
          />
        )}

        {/* DC-1469 follow-up: a first-class, always-visible entry to Contextual
            Validity. It was previously reachable only via a nudge banner buried
            inside the Bridge dashboard (and once vanished when CV had run) — a
            named readiness dimension must be discoverable from the journey
            landing. Own row, so it never crowds the header actions. */}
        <button
          type="button"
          className="cv-entry-card"
          onClick={() => setActiveTab('contextual-validity')}
          title="Open Contextual Validity — declare the problem and assess data fit for this journey"
        >
          <Target size={20} aria-hidden="true" className="cv-entry-icon" />
          <span className="cv-entry-text">
            <strong>Contextual Validity</strong>
            <span>Declare the problem this data must serve, then assess how well it fits.</span>
          </span>
          <span className="cv-entry-cta">Open &rarr;</span>
        </button>

        {/* S4e: the state-aware Action Plan entry, first-class beside Contextual
            Validity. The label is the plan's actual state — Generate when absent,
            Open when present — and a failed probe says "could not check" rather
            than fabricating absence. The Bridge Value-Drivers/Priority-Actions
            links remain contextual secondary entry points. */}
        <button
          type="button"
          className="cv-entry-card ap-entry-card"
          onClick={() => setActiveTab('action-plan')}
          title="Go to the Action Plan for this journey"
        >
          <ClipboardList size={20} aria-hidden="true" className="cv-entry-icon" />
          <span className="cv-entry-text">
            {actionPlanEntry === 'exists' && (
              <>
                <strong>Open Action Plan</strong>
                <span>Matrix-first prioritization of this journey&apos;s tasks — pick up where you left off.</span>
              </>
            )}
            {actionPlanEntry === 'absent' && (
              <>
                <strong>Generate Action Plan</strong>
                <span>Build one from this journey&apos;s Bridge AI recommendations. You review the value inputs first — nothing is priced silently.</span>
              </>
            )}
            {actionPlanEntry === 'checking' && (
              <>
                <strong>Action Plan</strong>
                <span>Checking for an existing plan&hellip;</span>
              </>
            )}
            {actionPlanEntry === 'unavailable' && (
              <>
                <strong>Action Plan</strong>
                <span>Could not check whether a plan exists — opening will tell you. Your plan, if one exists, is unaffected.</span>
              </>
            )}
          </span>
          {/* Four states, four CTAs — a binary absent/else made "checking" and
              "could not check" claim "Open →", a small fabricated certainty. */}
          <span className="cv-entry-cta">
            {actionPlanEntry === 'exists' && 'Open →'}
            {actionPlanEntry === 'absent' && 'Generate →'}
            {actionPlanEntry === 'checking' && 'Continue →'}
            {actionPlanEntry === 'unavailable' && 'Check in Action Plan →'}
          </span>
        </button>

      {/* Getting Started Guide (shown before any station has completed) */}
      {completedStations === 0 && (
        <div className="journey-guide">
          <Lightbulb size={24} aria-hidden="true" className="guide-icon-svg" />
          <div className="guide-content">
            <h3>Getting Started with Your Journey</h3>
            <p>Your journey is ready. Here's what you can do:</p>
            <ol>
              <li><strong>Review your datasets</strong> in the manifest below</li>
              <li><strong>Select a station</strong> from the pipeline to see details</li>
              <li><strong>Click "Start Station"</strong> to begin analysis</li>
              <li><strong>Track progress</strong> as stations complete and recommendations appear</li>
            </ol>
            <p className="guide-note">
              <strong>Tip:</strong> Required stations (marked with *) must be completed for a full FAIR assessment.
            </p>
          </div>
        </div>
      )}

      {/* Asset Manifest (formerly TrainVisualization — see DC-1521).
          No onAddAssets: assets are fixed at journey creation in v2.0, so the
          Add Dataset affordance is hidden rather than stubbed.
          v2.5 SS-02/B2 round 1: NO station counts here — the legacy Stations
          N/M tile was a second completeness denominator that could contradict
          the canonical contract (and showed numbers on workflow-unknown
          journeys). The status panel is the ONLY completeness surface. */}
      <TrainVisualization
        assets={journey.assets}
        onAssetClick={(asset) => setSelectedAsset(asset)}
        refreshTrigger={governanceRefreshTrigger}
      />

      {/* Split-View Navigator — v2.0: stepper + station run list (no Cytoscape) */}
      <div className="pipeline-dashboard-grid">
        {/* LEFT: stepper + run list (the prototype beat-2 Navigator) */}
        <div className="pipeline-visualizer">
          <div className="pipeline-header">
            <h3>Stations</h3>
            <span className="asset-badge">
              <Boxes size={14} aria-hidden="true" />
              {journey.assets.length} Asset{journey.assets.length !== 1 ? 's' : ''} in Pipeline
            </span>
          </div>
          <StationRunList
            stations={journey.stations}
            currentStationId={currentStationId}
            onStationSelect={(id) => setCurrentStationId(id)}
          />
        </div>

        {/* RIGHT: The Inspector (Action) */}
        <div className="pipeline-inspector">
          {currentStation ? (
            <StationActionPanel
              station={currentStation}
              journeyAssets={journey.assets}
              onRefresh={fetchJourneyDetails}
            />
          ) : (
            <div className="inspector-empty">
              <MousePointerClick size={32} aria-hidden="true" className="inspector-empty-icon" />
              <h3>Select a Station</h3>
              <p>Click a station to run analysis or view results.</p>
            </div>
          )}
        </div>
      </div>
            </div>
          </div>
        )}

        {/* FAIR Tab — Rules / Graph for HITL review */}
        {activeTab === 'fair' && (
          <div id="fair-panel" role="tabpanel" aria-labelledby="fair-tab" style={{ padding: 16 }}>
            <ErrorBoundary level="section" label="FAIR">
              <FairTabContent
                journey={journey}
                selectedFairAssetId={selectedFairAssetId}
                setSelectedFairAssetId={setSelectedFairAssetId}
              />
            </ErrorBoundary>
          </div>
        )}

        {/* Data Quality Tab — in-window scorecard (DC-1549) */}
        {activeTab === 'data-quality' && (
          <div id="data-quality-panel" role="tabpanel" aria-labelledby="data-quality-tab" style={{ padding: 16 }}>
            <ErrorBoundary level="section" label="Data Quality">
              <StationResultsTabContent
                stationId="data_quality"
                stationLabel="Data Quality"
                journey={journey}
                // DC-1591: pass group_id through so AnomalyPanel, DriftPanel,
                // and TextualPanel render in the in-window DQ tab — those
                // three are gated by `journeyId && <Panel ... />`, so dropping
                // the prop silently blanks 3 of 4 enhancement tabs.
                renderResults={(jobId) => (
                  <DataQualityScorecard jobId={jobId} journeyId={group_id || undefined} />
                )}
              />
            </ErrorBoundary>
          </div>
        )}

        {/* ML Readiness Tab — in-window analysis (DC-1549) */}
        {activeTab === 'ml-readiness' && (
          <div id="ml-readiness-panel" role="tabpanel" aria-labelledby="ml-readiness-tab" style={{ padding: 16 }}>
            <ErrorBoundary level="section" label="ML Readiness">
              <StationResultsTabContent
                stationId="ml_readiness"
                stationLabel="ML Readiness"
                journey={journey}
                renderResults={(jobId) => <MLReadinessLegacyWrapper jobId={jobId} />}
              />
            </ErrorBoundary>
          </div>
        )}

        {/* Ontology Inference Tab (DC-1569 #3) */}
        {activeTab === 'ontology-inference' && (
          <div id="ontology-inference-panel" role="tabpanel" aria-labelledby="ontology-inference-tab" style={{ padding: 16 }}>
            <ErrorBoundary level="section" label="Ontology Inference">
              <StationResultsTabContent
                stationId="ontology_inference"
                stationLabel="Ontology Inference"
                journey={journey}
                renderResults={(jobId) => (
                  <div className="scv-embed">
                    <SemanticClarityPanel jobId={jobId} journeyId={group_id || undefined} />
                  </div>
                )}
              />
            </ErrorBoundary>
          </div>
        )}

        {/* Governance Tab */}
        {activeTab === 'governance' && (
          <div id="governance-panel" role="tabpanel" aria-labelledby="governance-tab">
            <ErrorBoundary level="section" label="Governance">
            <JourneyGovernancePanel />
            </ErrorBoundary>
          </div>
        )}

        {/* Maturity Tab */}
        {activeTab === 'maturity' && (
          <div id="maturity-panel" role="tabpanel" aria-labelledby="maturity-tab">
            <ErrorBoundary level="section" label="Maturity Assessment">
            <JourneyMaturityPanel />
            </ErrorBoundary>
          </div>
        )}

        {/* Contextual Validity Tab (Phase 5) */}
        {activeTab === 'contextual-validity' && (
          <div id="contextual-validity-panel" role="tabpanel" aria-labelledby="contextual-validity-tab" className="contextual-validity-tab-content">
            <ErrorBoundary level="section" label="Contextual Validity">
            <ContextualFitPanel
              onConfigureProblem={() => {
                // Navigate to problem declaration form or open modal
                navigate(`/journey/${group_id}/problem-declaration`);
              }}
              onNavigateToBridge={() => setActiveTab('bridge')}
            />
            </ErrorBoundary>
          </div>
        )}

        {/* Recommendations Tab */}
        {activeTab === 'recommendations' && (
          <div id="recommendations-panel" role="tabpanel" aria-labelledby="recommendations-tab">
            <ErrorBoundary level="section" label="Recommendations">
              <RecommendationList groupId={group_id || ''} />
            </ErrorBoundary>
          </div>
        )}

        {/* Action Plan Tab */}
        {activeTab === 'action-plan' && (
          <div id="action-plan-panel" role="tabpanel" aria-labelledby="action-plan-tab">
            <ErrorBoundary level="section" label="Action Plan">
            <ActionPlanPanel />
            </ErrorBoundary>
          </div>
        )}

        {/* Settings Tab (DC-1569) */}
        {activeTab === 'settings' && (
          <div id="settings-panel" role="tabpanel" aria-labelledby="settings-tab">
            <ErrorBoundary level="section" label="Journey Settings">
              <JourneySettingsPanel />
            </ErrorBoundary>
          </div>
        )}
      </div>

      {/* Asset Detail Panel (slide-out) */}
      {selectedAsset && (
        <AssetDetailPanel
          asset={selectedAsset}
          onClose={() => setSelectedAsset(null)}
          onAssignmentChange={() => {
            // Trigger governance indicator refresh in TrainVisualization
            setGovernanceRefreshTrigger(prev => prev + 1);
          }}
        />
      )}
    </div>
    </JourneyProvider>
  );
};

// ============================================================================
// FAIR TAB CONTENT
// ============================================================================

interface FairTabContentProps {
  journey: JourneyDetails;
  selectedFairAssetId: string | null;
  setSelectedFairAssetId: (id: string | null) => void;
}

/**
 * FAIR tab body — HITL rules review over the live_checks station's job_id,
 * with an asset selector when the journey has multiple assets. The Rules/Graph
 * sub-tab toggle was retired in v2.0: the FAIR Core grade grid
 * (/analysis/:job/fair-core) replaced the Cytoscape assessment graph.
 *
 * Today the live_checks station carries a single job_id even for multi-asset
 * journeys (server-side limitation). The asset selector is wired up so
 * that once per-asset job_ids are available, switching it will swap the
 * data shown without further UI changes.
 */
const FairTabContent: React.FC<FairTabContentProps> = ({
  journey,
  selectedFairAssetId,
  setSelectedFairAssetId,
}) => {
  const fairStation = journey.stations?.find(
    (s) => s.station_id === 'live_checks' || s.station_id === 'fair_assessment',
  );
  const fairJobId = fairStation?.job_id;
  const isCompleted = fairStation?.status === 'completed';
  const datasetAssets = (journey.assets || []).filter((a) => a.type === 'dataset');
  const isMultiAsset = datasetAssets.length > 1;

  // Default-select the first dataset asset on first render so the selector
  // shows a value even though we don't yet have per-asset job_ids.
  useEffect(() => {
    if (!selectedFairAssetId && datasetAssets.length > 0) {
      setSelectedFairAssetId(datasetAssets[0].asset_id);
    }
  }, [selectedFairAssetId, datasetAssets, setSelectedFairAssetId]);

  if (!fairStation) {
    return (
      <div style={{ padding: 24, color: '#64748b' }}>
        <p>The FAIR Assessment station is not part of this journey.</p>
        <p style={{ fontSize: '0.85rem', marginTop: 8 }}>
          Add it to the journey to score the dataset against the 16 FAIR rules
          (F1–R1.3) — including F2 maturity tiers, with human-in-the-loop overrides.
        </p>
      </div>
    );
  }

  if (!isCompleted) {
    return (
      <div style={{ padding: 24, color: '#64748b' }}>
        <p>
          FAIR analysis is{' '}
          <strong>{fairStation.status === 'in_progress' ? 'still running' : 'not yet complete'}</strong>.
          Results will appear here once the FAIR station finishes — typically under
          a minute for Databricks tables, longer for file uploads.
        </p>
      </div>
    );
  }

  if (!fairJobId) {
    return (
      <div style={{ padding: 24, color: '#dc2626' }}>
        <p>
          The FAIR station completed but no job_id is attached. This usually means
          the run finished before the analysis_jobs row was persisted. Re-run the
          FAIR station to recover.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Asset selector (the Rules/Graph toggle is retired — the FAIR Core
          grade grid replaced the Cytoscape graph; this tab is the HITL rules
          review). */}
      {isMultiAsset && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 12,
            marginBottom: 12,
            flexWrap: 'wrap',
          }}
        >
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: 'var(--tri-body)' }}>
            <span>Asset:</span>
            <select
              value={selectedFairAssetId || ''}
              onChange={(e) => setSelectedFairAssetId(e.target.value || null)}
              style={{
                padding: '4px 8px',
                border: '1px solid var(--tri-taupe)',
                borderRadius: 4,
                fontSize: '0.85rem',
                background: 'var(--tri-card-2)',
              }}
            >
              {datasetAssets.map((a) => (
                <option key={a.asset_id} value={a.asset_id}>{a.name}</option>
              ))}
            </select>
            <span style={{ fontSize: '0.75rem', color: 'var(--tri-body)' }}>
              (showing aggregate; per-asset views land in v1.3)
            </span>
          </label>
        </div>
      )}

      <EvaluateContent jobId={fairJobId} embedded={true} groupId={journey.group_id} />
    </div>
  );
};

// ============================================================================
// STATION RESULTS TAB CONTENT (DC-1549)
//
// Generic shell that mounts a station's results component in-window once the
// station has completed and has a job_id attached. Mirrors FairTabContent's
// gating semantics so DQ + ML Readiness tabs behave consistently.
// ============================================================================

interface StationResultsTabContentProps {
  stationId: string;
  stationLabel: string;
  journey: JourneyDetails;
  renderResults: (jobId: string) => React.ReactNode;
}

const StationResultsTabContent: React.FC<StationResultsTabContentProps> = ({
  stationId,
  stationLabel,
  journey,
  renderResults,
}) => {
  const station = journey.stations?.find((s) => s.station_id === stationId);

  if (!station) {
    return (
      <div style={{ padding: 24, color: '#64748b' }}>
        <p>The {stationLabel} station is not part of this journey.</p>
        <p style={{ fontSize: '0.85rem', marginTop: 8 }}>
          Add it to the journey from the Stations tab to see results here.
        </p>
      </div>
    );
  }

  const isCompleted = station.status === 'completed';
  if (!isCompleted) {
    return (
      <div style={{ padding: 24, color: '#64748b' }}>
        <p>
          {stationLabel} analysis is{' '}
          <strong>{station.status === 'in_progress' ? 'still running' : 'not yet complete'}</strong>.
          Results will appear here once the station finishes.
        </p>
      </div>
    );
  }

  const jobId = station.job_id;
  if (!jobId) {
    return (
      <div style={{ padding: 24, color: '#dc2626' }}>
        <p>
          The {stationLabel} station completed but no job_id is attached. This usually
          means the run finished before the analysis_jobs row was persisted. Re-run
          the station to recover.
        </p>
      </div>
    );
  }

  return <>{renderResults(jobId)}</>;
};

export default JourneyNavigator;
